import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { X } from 'lucide-react';
import { Project } from '../types';
import { formatCurrency, formatEnergy, formatPercent, formatNumber } from '../utils/formatters';
import { motion, AnimatePresence } from 'motion/react';

interface CompareModalProps {
  isOpen: boolean;
  onClose: () => void;
  projects: Project[];
}

export function CompareModal({ isOpen, onClose, projects }: CompareModalProps) {
  if (!isOpen || projects.length === 0) return null;

  // Prepare comparison data
  const comparisonMetrics = projects.map(p => ({
    id: p.id,
    name: p.name,
    capacity: p.systemConfig.capacity,
    annualProduction: p.results!.energyProduction.annualProduction,
    npv: p.results!.financialMetrics.npv,
    irr: p.results!.financialMetrics.irr,
    payback: p.results!.financialMetrics.paybackPeriod,
    roi: p.results!.financialMetrics.roi,
    lcoe: p.results!.financialMetrics.lcoe,
    co2: p.results!.environmentalImpact.co2Avoided,
  }));

  const COLORS = ['#f59e0b', '#3b82f6', '#10b981'];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 cursor-pointer"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-7xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl border-2 border-slate-200 shadow-xl pointer-events-auto"
            >
              {/* Header */}
              <div className="sticky top-0 z-10 bg-white border-b-2 border-slate-200 px-8 py-6 flex items-center justify-between rounded-t-2xl">
                <div>
                  <h2 className="text-3xl font-bold text-slate-900">Project Comparison</h2>
                  <p className="text-slate-600 mt-1">Comparing {projects.length} solar projects</p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 rounded-xl transition-colors bg-slate-100 hover:bg-slate-200 border-2 border-slate-200"
                >
                  <X className="w-5 h-5 text-slate-700" />
                </button>
              </div>

              {/* Content */}
              <div className="p-8 space-y-8">
                {/* Comparison Table */}
                <div className="bg-white rounded-xl border-2 border-slate-200 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b-2 border-slate-200">
                        <tr>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Metric</th>
                          {projects.map((project, index) => (
                            <th key={project.id} className="px-6 py-4 text-left text-sm font-semibold" style={{ color: COLORS[index] }}>
                              {project.name}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        <ComparisonRow
                          label="System Capacity"
                          values={projects.map(p => ({ id: p.id, value: `${p.systemConfig.capacity} kWp` }))}
                          colors={COLORS}
                        />
                        <ComparisonRow
                          label="Annual Production"
                          values={projects.map(p => ({ id: p.id, value: formatEnergy(p.results!.energyProduction.annualProduction) }))}
                          colors={COLORS}
                          highlight="max"
                        />
                        <ComparisonRow
                          label="Specific Yield"
                          values={projects.map(p => ({ id: p.id, value: `${formatNumber(p.results!.energyProduction.specificYield, 0)} kWh/kWp/year` }))}
                          colors={COLORS}
                          highlight="max"
                        />
                        <ComparisonRow
                          label="NPV"
                          values={projects.map(p => ({ id: p.id, value: formatCurrency(p.results!.financialMetrics.npv, p.financialParams.currency) }))}
                          colors={COLORS}
                          highlight="max"
                        />
                        <ComparisonRow
                          label="IRR"
                          values={projects.map(p => ({ id: p.id, value: formatPercent(p.results!.financialMetrics.irr) }))}
                          colors={COLORS}
                          highlight="max"
                        />
                        <ComparisonRow
                          label="Payback Period"
                          values={projects.map(p => ({ id: p.id, value: `${formatNumber(p.results!.financialMetrics.paybackPeriod, 1)} years` }))}
                          colors={COLORS}
                          highlight="min"
                        />
                        <ComparisonRow
                          label="ROI"
                          values={projects.map(p => ({ id: p.id, value: formatPercent(p.results!.financialMetrics.roi) }))}
                          colors={COLORS}
                          highlight="max"
                        />
                        <ComparisonRow
                          label="LCOE"
                          values={projects.map(p => ({ id: p.id, value: `$${formatNumber(p.results!.financialMetrics.lcoe, 3)}/kWh` }))}
                          colors={COLORS}
                          highlight="min"
                        />
                        <ComparisonRow
                          label="CO₂ Avoided"
                          values={projects.map(p => ({ id: p.id, value: `${formatNumber(p.results!.environmentalImpact.co2Avoided, 0)} tons` }))}
                          colors={COLORS}
                          highlight="max"
                        />
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl border-2 border-slate-200 shadow-sm p-6">
                    <h3 className="font-semibold text-slate-900 mb-4">Financial Metrics</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={comparisonMetrics}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" stroke="#64748b" />
                        <YAxis stroke="#64748b" />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                        />
                        <Legend />
                        <Bar dataKey="npv" fill={COLORS[0]} name="NPV ($)" />
                        <Bar dataKey="irr" fill={COLORS[1]} name="IRR (%)" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="bg-white rounded-xl border-2 border-slate-200 shadow-sm p-6">
                    <h3 className="font-semibold text-slate-900 mb-4">Energy Production</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={comparisonMetrics}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" stroke="#64748b" />
                        <YAxis stroke="#64748b" />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                        />
                        <Legend />
                        <Bar dataKey="annualProduction" fill={COLORS[2]} name="Annual Production (kWh)" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="bg-white rounded-xl border-2 border-slate-200 shadow-sm p-6">
                    <h3 className="font-semibold text-slate-900 mb-4">Payback Period</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={comparisonMetrics}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" stroke="#64748b" />
                        <YAxis stroke="#64748b" />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                        />
                        <Legend />
                        <Bar dataKey="payback" fill={COLORS[0]} name="Payback Period (years)" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="bg-white rounded-xl border-2 border-slate-200 shadow-sm p-6">
                    <h3 className="font-semibold text-slate-900 mb-4">Environmental Impact</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={comparisonMetrics}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" stroke="#64748b" />
                        <YAxis stroke="#64748b" />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                        />
                        <Legend />
                        <Bar dataKey="co2" fill={COLORS[2]} name="CO₂ Avoided (tons)" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

interface ComparisonRowProps {
  label: string;
  values: { id: string, value: string }[];
  colors: string[];
  highlight?: 'max' | 'min';
}

function ComparisonRow({ label, values, colors, highlight }: ComparisonRowProps) {
  let bestIndex = -1;
  
  if (highlight) {
    const numericValues = values.map(v => parseFloat(v.value.replace(/[^0-9.-]/g, '')));
    if (highlight === 'max') {
      bestIndex = numericValues.indexOf(Math.max(...numericValues));
    } else {
      bestIndex = numericValues.indexOf(Math.min(...numericValues));
    }
  }

  return (
    <tr className="hover:bg-slate-50">
      <td className="px-6 py-4 text-sm font-medium text-slate-900">{label}</td>
      {values.map((item, index) => (
        <td
          key={item.id}
          className={`px-6 py-4 text-sm ${
            index === bestIndex ? 'font-bold' : 'font-medium'
          }`}
          style={{
            color: index === bestIndex ? colors[index] : '#475569',
            backgroundColor: index === bestIndex ? `${colors[index]}10` : 'transparent',
          }}
        >
          {item.value}
        </td>
      ))}
    </tr>
  );
}